import styled, { keyframes, css } from 'styled-components'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'

// ── Container & Header ──

export const PickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 95%;
`

export const PickerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const HeaderIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: #fff;
  }
`

export const HeaderTitle = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-primary);
`

// ── Card ──

export const PickerCard = styled.div`
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-chat-card-bg);
  padding: 12px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

// ── Top Row (counter + nav arrows) ──

export const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const CounterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const Counter = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.5px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
`

export const AddedBadge = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.5px;
  color: var(--color-primary);
  text-transform: uppercase;
`

export const NavArrows = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

export const NavArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-secondary);
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    opacity 0.15s;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: var(--color-bg-hover);
    border-color: var(--color-border-dark);
  }
`

// ── Content ──

export const CardContent = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.5;
  color: var(--color-text-primary);
  min-height: 40px;
`

export const FallbackTitle = styled.div`
  font-weight: ${FONT_WEIGHT.semibold};
  margin-bottom: 2px;
`

export const FallbackDescription = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.5;
  color: var(--color-text-secondary);

  p {
    margin: 0;
  }
`

// ── Action Buttons ──

export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const AddButton = styled.button<{ $added: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  ${(p) =>
    p.$added
      ? css`
          background: transparent;
          border: 1.5px solid var(--color-primary);
          color: var(--color-primary);
          cursor: default;
        `
      : css`
          background: var(--color-primary);
          border: 1.5px solid var(--color-primary);
          color: #fff;

          &:hover {
            background: var(--color-primary-hover);
            border-color: var(--color-primary-hover);
          }
        `}
`

export const SkipButton = styled.button`
  display: flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 6px;
  border: 1.5px solid var(--color-border);
  background: transparent;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  font-family: inherit;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: var(--color-bg-hover);
    border-color: var(--color-border-dark);
  }
`

// ── Progress Dots ──

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`

export const ProgressTrack = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding-top: 2px;
`

export const ProgressDot = styled.div<{
  $state: 'active' | 'added' | 'pending'
}>`
  height: 4px;
  border-radius: 2px;
  transition: all 0.25s ease;

  ${(p) => {
    switch (p.$state) {
      case 'active':
        return css`
          width: 16px;
          background: var(--color-primary);
          animation: ${fadeIn} 0.2s ease;
        `
      case 'added':
        return css`
          width: 4px;
          background: var(--color-primary);
          opacity: 0.7;
        `
      case 'pending':
        return css`
          width: 4px;
          background: var(--color-text-muted);
          opacity: 0.3;
        `
    }
  }}
`

// ── Loading state ──

export const LoadingBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
`
